import React from 'react';
import PropTypes from 'prop-types';
import { some } from 'lodash';

import {
  FORMIK_FINANCE_FIELD_IDS,
  SUPPORTED_IBAN_LANGUAGES,
} from '~/components/Contacts/UpsertContact/Tabs/Finance/constants';
import MenuItem from '~/components/core/Atomic/MenuItem';
import PermissionsButtonWrapper from '~/components/core/Permissions/PermissionsButtonWrapper';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { usePaymentsConfiguration } from '~/components/hooks/usePaymentsConfiguration';
import { TextFieldFormik } from '~/components/TextFieldFormik';
import { LANGUAGES_DICT } from '~/Types';
import { stringCmp } from '~/Utils';

import { getFullFinancePath } from './utils';

// One full-width nested grid per payment method, so each method's fields wrap among themselves only.
const PER_METHOD_BLOCK_CLASS_NAME = 'col-span-2 grid grid-cols-2 gap-20';

const Bank = ({ disabled }) => {
  const {
    isUkBankTransferEnabledForAny,
    isIbanBankTransferEnabledForAny,
    isAuBankTransferEnabledForAny,
    isNzBankTransferEnabledForAny,
    isSwiftBicEnabledForAnyMethod,
  } = usePaymentsConfiguration();

  if (
    !some([
      isUkBankTransferEnabledForAny,
      isIbanBankTransferEnabledForAny,
      isAuBankTransferEnabledForAny,
      isNzBankTransferEnabledForAny,
    ])
  ) {
    return null;
  }

  return (
    <div className="grid grid-cols-2 gap-20">
      {/* Each per-method block gets its own full-width nested grid, so a method with an odd number of
          fields cannot leave a half row open for the next method's first field to drop into. */}
      {isIbanBankTransferEnabledForAny ? (
        <div className={PER_METHOD_BLOCK_CLASS_NAME}>
          <div className="col-span-2">
            <PermissionsButtonWrapper action={PERMISSION_ACTIONS.CONTACT_FINANCE_PII} verb={PERMISSION_VERBS.WRITE}>
              <TextFieldFormik
                id={getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.IBAN_LANGUAGE)}
                label="Language"
                fullWidth
                disabled={disabled}
                select
              >
                {SUPPORTED_IBAN_LANGUAGES.sort((lang1, lang2) =>
                  stringCmp(LANGUAGES_DICT[lang1], LANGUAGES_DICT[lang2])
                ).map((language) => (
                  <MenuItem key={language} value={language}>
                    {LANGUAGES_DICT[language]}
                  </MenuItem>
                ))}

                <MenuItem value="other">Other</MenuItem>
              </TextFieldFormik>
            </PermissionsButtonWrapper>
          </div>
          <PermissionsButtonWrapper action={PERMISSION_ACTIONS.CONTACT_FINANCE_PII} verb={PERMISSION_VERBS.WRITE}>
            <TextFieldFormik
              id={getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.IBAN)}
              label="IBAN"
              fullWidth
              disabled={disabled}
            />
          </PermissionsButtonWrapper>
        </div>
      ) : null}
      {isUkBankTransferEnabledForAny ? (
        <div className={PER_METHOD_BLOCK_CLASS_NAME}>
          <PermissionsButtonWrapper action={PERMISSION_ACTIONS.CONTACT_FINANCE_PII} verb={PERMISSION_VERBS.WRITE}>
            <TextFieldFormik
              id={getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.UK_BANK_SORT_CODE)}
              label="Sort Code"
              fullWidth
              disabled={disabled}
            />
          </PermissionsButtonWrapper>
          <PermissionsButtonWrapper action={PERMISSION_ACTIONS.CONTACT_FINANCE_PII} verb={PERMISSION_VERBS.WRITE}>
            <TextFieldFormik
              id={getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.UK_BANK_ACCOUNT_NUMBER)}
              label="Account Number"
              fullWidth
              disabled={disabled}
            />
          </PermissionsButtonWrapper>
        </div>
      ) : null}
      {isAuBankTransferEnabledForAny ? (
        <div className={PER_METHOD_BLOCK_CLASS_NAME}>
          <PermissionsButtonWrapper action={PERMISSION_ACTIONS.CONTACT_FINANCE_PII} verb={PERMISSION_VERBS.WRITE}>
            <TextFieldFormik
              id={getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.AU_BSB_NUMBER)}
              label="BSB Number"
              fullWidth
              disabled={disabled}
            />
          </PermissionsButtonWrapper>
          <PermissionsButtonWrapper action={PERMISSION_ACTIONS.CONTACT_FINANCE_PII} verb={PERMISSION_VERBS.WRITE}>
            <TextFieldFormik
              id={getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.AU_BANK_ACCOUNT_NUMBER)}
              label="Account Number"
              fullWidth
              disabled={disabled}
            />
          </PermissionsButtonWrapper>
        </div>
      ) : null}
      {isNzBankTransferEnabledForAny ? (
        <div className={PER_METHOD_BLOCK_CLASS_NAME}>
          <PermissionsButtonWrapper action={PERMISSION_ACTIONS.CONTACT_FINANCE_PII} verb={PERMISSION_VERBS.WRITE}>
            <TextFieldFormik
              id={getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.NZ_BANK_ACCOUNT_NUMBER)}
              label="NZ Bank Account"
              fullWidth
              disabled={disabled}
            />
          </PermissionsButtonWrapper>
        </div>
      ) : null}
      {/* Shared across IBAN, AU and NZ bank transfer, so it trails the per-method blocks rather
          than sitting inside one of them -- placing it earlier would interpose it between the
          fields of a method that does not use it, such as UK bank transfer. It is a direct child of
          the outer grid, so it starts a row of its own after the last per-method block. */}
      {isSwiftBicEnabledForAnyMethod ? (
        <PermissionsButtonWrapper action={PERMISSION_ACTIONS.CONTACT_FINANCE_PII} verb={PERMISSION_VERBS.WRITE}>
          <TextFieldFormik
            id={getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.SWIFT_BIC)}
            label="SWIFT / BIC"
            fullWidth
            disabled={disabled}
          />
        </PermissionsButtonWrapper>
      ) : null}
      <div className="col-span-2">
        <PermissionsButtonWrapper action={PERMISSION_ACTIONS.CONTACT_FINANCE_PII} verb={PERMISSION_VERBS.WRITE}>
          <TextFieldFormik
            id={getFullFinancePath(FORMIK_FINANCE_FIELD_IDS.ACCOUNT_DETAILS)}
            label="Account details"
            fullWidth
            multiline
            rows={3}
            disabled={disabled}
            inputProps={{ maxLength: 1024 }}
          />
        </PermissionsButtonWrapper>
      </div>
    </div>
  );
};

Bank.propTypes = {
  disabled: PropTypes.bool,
};

export default Bank;
